import { useState, useEffect, useCallback, useMemo } from 'react';
import { Modal, Select, InputNumber, Input, Button, Table, Tag, Alert, Empty, message, Tooltip } from 'antd';
import { PlusOutlined, DeleteOutlined, SaveOutlined, PrinterOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { cuttingPlannerApi } from './cuttingPlannerApi';
import { formatThickness } from './cuttingPlannerHelpers';
import { offcutApi } from '../../inventory/offcuts/offcutApi';
import { offcutArea, formatArea } from '../../inventory/offcuts/offcutHelpers';

// ════════════════════════════════════════════════════════════════════
// Record off-cuts generated by a cutting plan.
//
// The glass is picked from the stock the plan actually consumed (issued
// sheets) so the off-cut is stamped with a valid item + thickness and linked
// to the plan (source_cutting_plan_id + source_opt_no). The operator adds one
// or more piece sizes; each row's Qty is saved by the backend as that many
// INDIVIDUAL single-piece off-cut rows — each with its own id + OC-code — so
// every remnant is tracked separately. Off-cuts also appear in the Inventory
// Off-cut register (same table), now plan-linked.
// ════════════════════════════════════════════════════════════════════

function backendError(err, fallback) {
  const e = err && err.response && err.response.data && err.response.data.error;
  return (e && e.message) || (err && err.message) || fallback || 'Something went wrong.';
}

let ROW_SEQ = 0;
const newRow = () => ({ key: `r${++ROW_SEQ}`, width: null, height: null, qty: 1 });

export default function PlanOffcutModal({ open, planId, planNo, canRecord, canPrint, onClose, onChanged }) {
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(false);
  const [itemId, setItemId] = useState(null);
  const [rows, setRows] = useState([newRow()]);
  const [warehouse, setWarehouse] = useState('');
  const [rack, setRack] = useState('');
  const [remarks, setRemarks] = useState('');
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Off-cuts already recorded against this plan (for the print list).
  const [recorded, setRecorded] = useState([]);
  const [loadingRecorded, setLoadingRecorded] = useState(false);
  const [printingAll, setPrintingAll] = useState(false);

  const load = useCallback(async () => {
    if (!planId) return;
    setLoading(true);
    try { setCtx(await cuttingPlannerApi.offcutContext(planId)); }
    catch (err) { message.error(backendError(err, 'Could not load the plan.')); }
    finally { setLoading(false); }
  }, [planId]);

  const loadRecorded = useCallback(async () => {
    if (!planId) return;
    setLoadingRecorded(true);
    try {
      const out = await offcutApi.list({ sourceCuttingPlanId: planId, pageSize: 200 });
      setRecorded((out && out.rows) || []);
    } catch (_e) { setRecorded([]); }
    finally { setLoadingRecorded(false); }
  }, [planId]);

  useEffect(() => {
    if (open) {
      setItemId(null); setRows([newRow()]); setWarehouse(''); setRack(''); setRemarks(''); setDirty(false);
      load();
      loadRecorded();
    }
  }, [open, load, loadRecorded]);

  // Open printable HTML (single or batch) in a new tab via a blob URL —
  // matches the OffcutsPage / WorkOrderScan sticker print pattern.
  const openHtml = useCallback((html) => {
    const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
    const win = window.open(url, '_blank');
    if (!win) message.warning('Allow pop-ups to print the labels.');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  }, []);

  const printOne = useCallback(async (id) => {
    try { openHtml(await offcutApi.labelHtml(id)); }
    catch (err) { message.error(backendError(err, 'Could not open the label.')); }
  }, [openHtml]);

  const printAll = useCallback(async () => {
    if (recorded.length === 0) return;
    setPrintingAll(true);
    try { openHtml(await offcutApi.labelsHtml({ sourceCuttingPlanId: planId })); }
    catch (err) { message.error(backendError(err, 'Could not open the labels.')); }
    finally { setPrintingAll(false); }
  }, [recorded.length, planId, openHtml]);

  const glassOptions = useMemo(() => (ctx && ctx.glassOptions) || [], [ctx]);

  // Auto-select the only glass option so the common case is one click.
  useEffect(() => {
    if (glassOptions.length === 1) setItemId(glassOptions[0].itemId);
  }, [glassOptions]);

  const selected = useMemo(
    () => glassOptions.find((g) => g.itemId === itemId) || null,
    [glassOptions, itemId]
  );

  const setRow = (key, patch) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const addRow = () => setRows((rs) => [...rs, newRow()]);
  const removeRow = (key) => setRows((rs) => (rs.length > 1 ? rs.filter((r) => r.key !== key) : rs));

  // Totals for the footer/preview.
  const totals = useMemo(() => {
    let pcs = 0, sqmt = 0;
    for (const r of rows) {
      const q = Math.max(0, Math.trunc(Number(r.qty) || 0));
      const a = offcutArea(r.width, r.height);
      if (r.width > 0 && r.height > 0 && q > 0) { pcs += q; sqmt += a.sqmt * q; }
    }
    return { pcs, sqmt };
  }, [rows]);

  const validRows = useMemo(
    () => rows.filter((r) => Number(r.width) > 0 && Number(r.height) > 0 && Math.trunc(Number(r.qty)) >= 1),
    [rows]
  );
  const canSubmit = canRecord && !!selected && validRows.length > 0 && !busy;

  const doSave = useCallback(async () => {
    if (!selected || validRows.length === 0) return;
    setBusy(true);
    try {
      let created = 0;
      // One create call per size row; the backend splits Qty into individual
      // single-piece rows (unique id + OC-code each).
      for (const r of validRows) {
        const out = await offcutApi.create({
          itemId: selected.itemId,
          thicknessMm: selected.thicknessMm != null ? selected.thicknessMm : undefined,
          widthMm: Number(r.width),
          heightMm: Number(r.height),
          qty: Math.trunc(Number(r.qty)) || 1,
          sourceCuttingPlanId: planId,
          sourceOptNo: ctx && ctx.plan ? ctx.plan.optNo : undefined,
          warehouseName: warehouse || undefined,
          rackName: rack || undefined,
          remarks: remarks || undefined
        });
        created += (out && out.count) || 0;
      }
      message.success(`Recorded ${created} off-cut piece${created === 1 ? '' : 's'} against ${planNo || 'the plan'}.`);
      setDirty(true);
      setRows([newRow()]);
      loadRecorded();
    } catch (err) {
      message.error(backendError(err, 'Could not record the off-cuts.'));
    } finally {
      setBusy(false);
    }
  }, [selected, validRows, planId, ctx, warehouse, rack, remarks, planNo, loadRecorded]);

  const handleClose = useCallback(() => { if (dirty && onChanged) onChanged(); onClose && onClose(); }, [dirty, onChanged, onClose]);

  const columns = [
    { title: 'Width (mm)', key: 'w', width: 130,
      render: (_v, r) => (
        <InputNumber min={1} step={1} value={r.width} onChange={(v) => setRow(r.key, { width: v })}
          disabled={!canRecord} style={{ width: '100%' }} placeholder="0" />
      ) },
    { title: 'Height (mm)', key: 'h', width: 130,
      render: (_v, r) => (
        <InputNumber min={1} step={1} value={r.height} onChange={(v) => setRow(r.key, { height: v })}
          disabled={!canRecord} style={{ width: '100%' }} placeholder="0" />
      ) },
    { title: 'Qty (pcs)', key: 'q', width: 100,
      render: (_v, r) => (
        <InputNumber min={1} step={1} precision={0} value={r.qty} onChange={(v) => setRow(r.key, { qty: v })}
          disabled={!canRecord} style={{ width: '100%' }} placeholder="1" />
      ) },
    { title: 'Area each', key: 'a', width: 130, align: 'right',
      render: (_v, r) => {
        if (!(r.width > 0) || !(r.height > 0)) return <span className="text-ink-300">—</span>;
        const a = offcutArea(r.width, r.height);
        return <span className="tabular-nums text-[12px] text-ink-500">{formatArea(a.sqmt)} m²</span>;
      } },
    { title: '', key: 'x', width: 44, align: 'center',
      render: (_v, r) => (
        <Button size="small" type="text" danger icon={<DeleteOutlined />} disabled={!canRecord || rows.length <= 1}
          onClick={() => removeRow(r.key)} aria-label="Remove size" />
      ) }
  ];

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      width={720}
      title={<span className="text-[15px] font-semibold text-ink-800">Record off-cuts · <span className="text-brand-600">{planNo || ''}</span></span>}
      footer={[
        <Button key="close" onClick={handleClose}>Close</Button>,
        <Tooltip key="save" title={!selected ? 'Pick the glass first' : (validRows.length === 0 ? 'Enter at least one size' : '')}>
          <Button type="primary" icon={<SaveOutlined />} onClick={doSave} loading={busy} disabled={!canSubmit}>
            Record {totals.pcs > 0 ? `${totals.pcs} pc${totals.pcs === 1 ? '' : 's'}` : ''}
          </Button>
        </Tooltip>
      ]}
      destroyOnClose
    >
      {!canRecord ? (
        <Alert type="info" showIcon className="!mb-3" message="You have read-only access — recording off-cuts needs the off-cut create permission (erp.inventory.offcuts.create)." />
      ) : null}

      {!loading && glassOptions.length === 0 ? (
        <Alert type="warning" showIcon className="!mb-3"
          message="No stock has been issued to this plan yet."
          description="Issue the sheets consumed by this plan first (Issue stock to production), then record the off-cuts here. You can also record off-cuts directly from Inventory → Off-cuts." />
      ) : null}

      <div className="text-[11px] font-semibold uppercase tracking-wide text-ink-400 mb-1.5">1 · Glass (from the sheets issued to this plan)</div>
      <Select
        className="w-full mb-1"
        placeholder={loading ? 'Loading…' : 'Select the glass this off-cut came from'}
        loading={loading}
        value={itemId || undefined}
        onChange={setItemId}
        disabled={!canRecord || glassOptions.length === 0}
        options={glassOptions.map((g) => ({
          value: g.itemId,
          label: `${g.itemName || 'Glass'}${g.thicknessMm != null ? ` · ${formatThickness(g.thicknessMm)}mm` : ''} · ${g.sheetsIssued} sheet${g.sheetsIssued === 1 ? '' : 's'} issued`
        }))}
        notFoundContent={<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No issued glass" />}
      />
      {selected ? (
        <div className="mb-3">
          <Tag className="!m-0 !text-[11px]">
            {selected.itemName}{selected.thicknessMm != null ? ` · ${formatThickness(selected.thicknessMm)}mm` : ''}
          </Tag>
        </div>
      ) : <div className="mb-3" />}

      <div className="text-[11px] font-semibold uppercase tracking-wide text-ink-400 mb-1.5">2 · Off-cut sizes</div>
      <Table
        className="cutting-grid" size="small" rowKey="key"
        columns={columns} dataSource={rows} pagination={false}
      />
      <Button size="small" type="dashed" icon={<PlusOutlined />} className="!mt-2" onClick={addRow} disabled={!canRecord}>
        Add another size
      </Button>

      <div className="mt-4 grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-ink-400">Warehouse (optional)</span>
          <Input value={warehouse} onChange={(e) => setWarehouse(e.target.value)} disabled={!canRecord} maxLength={160} placeholder="e.g. Main store" />
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-ink-400">Rack (optional)</span>
          <Input value={rack} onChange={(e) => setRack(e.target.value)} disabled={!canRecord} maxLength={120} placeholder="e.g. R-12" />
        </div>
      </div>
      <div className="mt-3 flex flex-col gap-1">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-ink-400">Remarks (optional)</span>
        <Input value={remarks} onChange={(e) => setRemarks(e.target.value)} disabled={!canRecord} maxLength={500} placeholder="e.g. usable corner off-cut" />
      </div>

      <div className="mt-4 rounded-lg border border-surface-200 bg-surface-50 px-4 py-2.5 flex items-center gap-4 text-[12px]">
        <span className="text-ink-500">Total to record:</span>
        <span className="font-semibold text-ink-800 tabular-nums">{totals.pcs} piece{totals.pcs === 1 ? '' : 's'}</span>
        <span className="text-ink-400">·</span>
        <span className="font-semibold text-ink-800 tabular-nums">{formatArea(totals.sqmt)} m²</span>
        <span className="text-ink-400 ml-auto text-[11px]">Qty &gt; 1 is saved as individual tracked pieces.</span>
      </div>

      {/* Recorded off-cuts for this plan — 50×50 mm labels, print one or all. */}
      <div className="mt-5 flex items-center gap-2">
        <PrinterOutlined className="text-brand-500 text-[14px]" />
        <span className="text-[11px] font-semibold uppercase tracking-wide text-ink-400">Recorded off-cuts for this plan</span>
        {recorded.length ? <Tag color="blue" className="!m-0 tabular-nums">{recorded.length}</Tag> : null}
        <div className="flex-1" />
        {canPrint ? (
          <Tooltip title={recorded.length ? 'Open all labels (50×50 mm) in one printable tab' : 'No off-cuts to print yet'}>
            <Button size="small" type="primary" ghost icon={<PrinterOutlined />} onClick={printAll}
              loading={printingAll} disabled={recorded.length === 0}>
              Print all labels
            </Button>
          </Tooltip>
        ) : null}
      </div>
      <Table
        className="cutting-grid mt-1.5" size="small" rowKey="id" loading={loadingRecorded}
        dataSource={recorded} pagination={false} scroll={{ y: 180 }}
        columns={[
          { title: 'Code', dataIndex: 'offcutCode', key: 'code', width: 168,
            render: (v) => <span className="tabular-nums text-[12px] font-medium">{v}</span> },
          { title: 'Size (mm)', key: 'size', width: 140,
            render: (_v, r) => <span className="tabular-nums text-[12px]">{`${Math.round(r.widthMm)} × ${Math.round(r.heightMm)}`}{r.thicknessMm ? ` · ${formatThickness(r.thicknessMm)}mm` : ''}</span> },
          { title: 'Recorded', dataIndex: 'createdAt', key: 'when', width: 118,
            render: (v) => (v ? dayjs(v).format('DD MMM HH:mm') : '—') },
          { title: '', key: 'x', width: 74, align: 'center',
            render: (_v, r) => canPrint
              ? <Button size="small" type="text" icon={<PrinterOutlined />} onClick={() => printOne(r.id)}>Label</Button>
              : null }
        ]}
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No off-cuts recorded for this plan yet." /> }}
      />
    </Modal>
  );
}
